/**
 * Shift Tic Tac Toe - Redux Slice
 * Manages game state, settings, and multiplayer info
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  createInitialState,
  placePiece as placeGamePiece,
  shiftPiece as shiftGamePiece,
  selectPiece as selectGamePiece,
  deselectPiece,
  resolveBlockedShiftTurn,
} from '../engine/gameLogic';
import { getAIMoveWithDelay } from '../engine/aiEngine';

// Initial state for the slice
const initialState = {
  gameMode: null, // 'ai' | 'online' | 'offline'
  difficulty: 'medium', // 'easy' | 'medium' | 'hard'
  gameState: null, // Core game state from gameLogic
  playerRole: 'X', // Player's piece in the game
  
  // Online multiplayer state
  roomCode: null,
  playerId: null,
  opponentJoined: false,
  opponentId: null,
  isHost: false,
  connectionStatus: 'disconnected', // 'connecting' | 'connected' | 'disconnected'
  
  // Settings
  settings: {
    maxPieces: 4,
    allowDiagonal: true,
    timerEnabled: false,
    timerSeconds: 30,
    soundEnabled: true,
    hapticEnabled: true,
  },
  
  // UI state
  isAIThinking: false,
  showTurnTransition: false,
  passPlayIntroDismissed: false,
  pendingRematch: null, // 'requested' | 'waiting' | null
  
  // Error handling
  error: null,
};

// Async thunk for AI move
export const makeAIMove = createAsyncThunk(
  'shiftGame/makeAIMove',
  async ({ difficulty, aiPlayer }, { getState }) => {
    const state = getState().shiftGame;
    if (!state.gameState) return null;
    
    const move = await getAIMoveWithDelay(state.gameState, difficulty, aiPlayer, 600);
    return move;
  }
);

const gameSlice = createSlice({
  name: 'shiftGame',
  initialState,
  reducers: {
    // Start a new game
    startGame: (state, action) => {
      const { mode, difficulty, playerRole, settings, startingPlayer } =
        action.payload;

      state.gameMode = mode;
      state.difficulty = difficulty || 'medium';
      state.playerRole = playerRole || 'X';
      state.error = null;
      state.pendingRematch = null;

      // Merge settings
      if (settings) {
        state.settings = { ...state.settings, ...settings };
      }

      const opener =
        startingPlayer ||
        settings?.startingPlayer ||
        'X';

      // Create initial game state
      state.gameState = createInitialState({
        maxPieces: state.settings.maxPieces,
        allowDiagonal: state.settings.allowDiagonal,
        startingPlayer: opener,
      });
      
      // Reset multiplayer state for non-online games
      if (mode !== 'online') {
        state.roomCode = null;
        state.opponentJoined = false;
        state.opponentId = null;
        state.connectionStatus = 'disconnected';
      }
      
      state.passPlayIntroDismissed = false;
      state.showTurnTransition = mode === 'offline';
      state.isAIThinking = false;
    },
    
    // Place a piece (placement phase)
    placePiece: (state, action) => {
      const { position, player } = action.payload;
      if (!state.gameState) return;
      
      const newState = placeGamePiece(state.gameState, position, player);
      if (newState.error) {
        state.error = newState.error;
        return;
      }

      state.gameState = newState;
      state.error = null;
    },

    // Select a piece for shifting (shift phase)
    selectPiece: (state, action) => {
      const { position, player } = action.payload;
      if (!state.gameState) return;
      
      const newState = selectGamePiece(state.gameState, position, player);
      if (newState.error) {
        state.error = newState.error;
        return;
      }
      
      state.gameState = newState;
      state.error = null;
    },
    
    // Deselect the currently selected piece
    clearSelection: (state) => {
      if (!state.gameState) return;
      state.gameState = deselectPiece(state.gameState);
      state.error = null;
    },
    
    // Shift a piece (shift phase)
    shiftPiece: (state, action) => {
      const { from, to, player } = action.payload;
      if (!state.gameState) return;
      
      const newState = shiftGamePiece(state.gameState, from, to, player);
      if (newState.error) {
        state.error = newState.error;
        return;
      }

      state.gameState = newState;
      state.error = null;
    },
    
    // Reset the current game
    resetGame: (state, action) => {
      if (!state.gameState) return;

      const startingPlayer = action.payload?.startingPlayer || 'X';

      state.gameState = createInitialState({
        maxPieces: state.settings.maxPieces,
        allowDiagonal: state.settings.allowDiagonal,
        startingPlayer,
      });
      state.error = null;
      state.pendingRematch = null;
      state.passPlayIntroDismissed = false;
      state.showTurnTransition = state.gameMode === 'offline';
      state.isAIThinking = false;
    },
    
    // Update game settings
    updateSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    
    // Set room code for online multiplayer
    setRoomCode: (state, action) => {
      state.roomCode = action.payload;
    },
    
    // Set player ID
    setPlayerId: (state, action) => {
      state.playerId = action.payload;
    },
    
    // Set host status
    setIsHost: (state, action) => {
      state.isHost = action.payload;
    },
    
    // Set opponent joined status
    setOpponentJoined: (state, action) => {
      state.opponentJoined = action.payload.joined;
      state.opponentId = action.payload.opponentId || null;
    },
    
    // Set connection status
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    
    // Sync game state from Firebase
    syncGameState: (state, action) => {
      const incoming = JSON.parse(JSON.stringify(action.payload));
      state.gameState = resolveBlockedShiftTurn(incoming);
      state.error = null;
    },
    
    // Set AI thinking state
    setAIThinking: (state, action) => {
      state.isAIThinking = action.payload;
    },
    
    // Dismiss pass-and-play intro overlay (shown once per game)
    dismissTurnTransition: (state) => {
      state.showTurnTransition = false;
      state.passPlayIntroDismissed = true;
    },

    /** Lobby PVP: ensure mode/role without resetting the board */
    setLobbyPvpSession: (state, action) => {
      const role = action.payload?.playerRole || state.playerRole || 'X';
      state.gameMode = 'pvp';
      state.playerRole = role;
      state.showTurnTransition = false;
      state.isAIThinking = false;
      state.error = null;
    },
    
    // Request rematch
    requestRematch: (state) => {
      state.pendingRematch = 'requested';
    },
    
    // Set rematch waiting (opponent requested)
    setRematchWaiting: (state) => {
      state.pendingRematch = 'waiting';
    },
    
    // Clear rematch state
    clearRematch: (state) => {
      state.pendingRematch = null;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Exit game and reset all state
    exitGame: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(makeAIMove.pending, (state) => {
        state.isAIThinking = true;
      })
      .addCase(makeAIMove.fulfilled, (state, action) => {
        state.isAIThinking = false;
        const move = action.payload;

        if (!state.gameState) return;

        const aiPlayer = state.playerRole === 'X' ? 'O' : 'X';

        if (!move) {
          state.gameState = resolveBlockedShiftTurn(state.gameState);
          return;
        }
        
        if (move.type === 'place') {
          const newState = placeGamePiece(state.gameState, move.position, aiPlayer);
          if (!newState.error) {
            state.gameState = newState;
          }
        } else if (move.type === 'shift') {
          const newState = shiftGamePiece(state.gameState, move.from, move.to, aiPlayer);
          if (!newState.error) {
            state.gameState = newState;
          }
        }
      })
      .addCase(makeAIMove.rejected, (state, action) => {
        state.isAIThinking = false;
        state.error = 'AI move failed';
      });
  },
});

// Export actions
export const {
  startGame,
  placePiece,
  selectPiece,
  clearSelection,
  shiftPiece,
  resetGame,
  updateSettings,
  setRoomCode,
  setPlayerId,
  setIsHost,
  setOpponentJoined,
  setConnectionStatus,
  syncGameState,
  setAIThinking,
  dismissTurnTransition,
  setLobbyPvpSession,
  requestRematch,
  setRematchWaiting,
  clearRematch,
  clearError,
  exitGame,
} = gameSlice.actions;

// Selectors
export const selectGameState = (state) => state.shiftGame.gameState;
export const selectGameMode = (state) => state.shiftGame.gameMode;
export const selectDifficulty = (state) => state.shiftGame.difficulty;
export const selectPlayerRole = (state) => state.shiftGame.playerRole;
export const selectSettings = (state) => state.shiftGame.settings;
export const selectIsAIThinking = (state) => state.shiftGame.isAIThinking;
export const selectShowTurnTransition = (state) => state.shiftGame.showTurnTransition;
export const selectError = (state) => state.shiftGame.error;
export const selectRoomCode = (state) => state.shiftGame.roomCode;
export const selectOpponentJoined = (state) => state.shiftGame.opponentJoined;
export const selectConnectionStatus = (state) => state.shiftGame.connectionStatus;
export const selectPendingRematch = (state) => state.shiftGame.pendingRematch;
export const selectIsHost = (state) => state.shiftGame.isHost;

// Complex selectors
export const selectIsMyTurn = (state) => {
  const gameState = state.shiftGame.gameState;
  const playerRole = state.shiftGame.playerRole;
  const gameMode = state.shiftGame.gameMode;
  
  if (!gameState) return false;
  if (gameState.winner || gameState.isDraw) return false;
  
  // Pass & play on one device — either player can act on their turn
  if (gameMode === 'offline') return true;

  // Lobby PVP and room-code online — only your piece
  if (gameMode === 'pvp' || gameMode === 'online') {
    return gameState.currentPlayer === playerRole;
  }

  return gameState.currentPlayer === playerRole;
};

export const selectCanMakeMove = (state) => {
  const isMyTurn = selectIsMyTurn(state);
  const isAIThinking = state.shiftGame.isAIThinking;
  const showTurnTransition = state.shiftGame.showTurnTransition;
  
  return isMyTurn && !isAIThinking && !showTurnTransition;
};

export default gameSlice.reducer;
